import { type Store, createLens } from '@frontend/viewer-core';

import { measureBetween } from './caliper';
import type { SnapResolver } from './snapResolver';
import {
  EMPTY_MEASURE,
  type DrawingModel,
  type DxfSessionState,
  type MeasureManager,
  type MeasureModifiers,
  type MeasureState,
  type MeasureTarget,
} from './types';

export const createMeasureSlice = (
  state: Store<DxfSessionState>,
): Store<MeasureState> =>
  createLens(
    state,
    (current) => current.measure,
    (current, measure) => ({ ...current, measure }),
  );

type Dependencies = {
  measure: Store<MeasureState>;
  /** Live tier. The resolver reads the same store for the free point. */
  modifiers: Store<MeasureModifiers>;
  resolve: SnapResolver;
  getModel: () => DrawingModel | null;
};

export const createMeasureController = ({
  measure,
  modifiers,
  resolve,
  getModel,
}: Dependencies): MeasureManager => {
  const measureTo = (from: MeasureTarget, to: MeasureTarget) =>
    measureBetween(from, to, {
      tolerance: getModel()?.samePointTolerance ?? 0,
      ...modifiers.getState(),
    });

  return {
    getState: measure.getState,
    subscribe: measure.subscribe,
    modifiers: { getState: modifiers.getState, subscribe: modifiers.subscribe },
    setModifiers(next) {
      modifiers.setState(() => next);
    },
    commitPoint(screenPoint) {
      const { target } = resolve(screenPoint);
      if (!target) {
        return;
      }
      measure.setState((previous) => {
        // A third click starts over rather than extending: a measurement is
        // between two targets, and the previous one is cleared as the new one
        // begins.
        if (previous.from === null || previous.measurement !== null) {
          return { from: target, measurement: null };
        }
        return {
          from: previous.from,
          measurement: measureTo(previous.from, target),
        };
      });
    },

    preview(target) {
      const { from, measurement } = measure.getState();
      return from && !measurement && target ? measureTo(from, target) : null;
    },

    clear() {
      measure.setState((previous) =>
        previous.from === null && previous.measurement === null
          ? previous
          : EMPTY_MEASURE,
      );
    },
  };
};
